import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaFacebook,
  FaTwitter,
  FaLinkedin,
  FaPinterest,
  FaWhatsapp,
  FaInstagram,
} from "react-icons/fa";
import DOMPurify from "dompurify";
import { Helmet } from "react-helmet";

const BlogDetail = () => {
	const [blog, setBlog] = useState(null);
	const [relatedBlogs, setRelatedBlogs] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [newsletterEmail, setNewsletterEmail] = useState("");

	// Get blog slug from URL
	const slug = window.location.pathname.split("/").pop(); // renamed from blogId

	useEffect(() => {
		setLoading(true);
		fetch(`${process.env.REACT_APP_API_BASE_URL}/api/blogs/${slug}/`) // now fetching by slug
			.then((res) => {
				if (!res.ok) throw new Error("Network response was not ok");
				return res.json();
			})
			.then((data) => {
				setBlog(data);
				setLoading(false);
			})
			.catch((err) => {
				setError(err.message);
				setLoading(false);
			});
		// Fetch related blogs using slug
		fetch(`${process.env.REACT_APP_API_BASE_URL}/api/blogs/?exclude=${slug}`) // now excluding by slug
			.then((res) => {
				if (!res.ok) throw new Error("Network response was not ok");
				return res.json();
			})
			.then((data) => setRelatedBlogs(data))
			.catch(() => {});
	}, [slug]); // updated dependency

	// New effect to load Disqus comments when blog is available
	useEffect(() => {
		if (blog) {
			window.disqus_config = function () {
				this.page.url = window.location.href;
				this.page.identifier = blog.slug;
			};
			const d = document;
			const s = d.createElement("script");
			s.src = "https://sunrisingcinevision.disqus.com/embed.js"; // replace YOUR_DISQUS_SHORTNAME
			s.setAttribute("data-timestamp", +new Date());
			s.async = true;
			(d.head || d.body).appendChild(s);
		}
	}, [blog]);

	const handleNewsletterSubmit = (e) => {
		e.preventDefault();
		console.log("Submitting newsletter email:", newsletterEmail); // debug log
		fetch(`${process.env.REACT_APP_API_BASE_URL}/api/newsletter/`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json", // added Accept header
			},
			body: JSON.stringify({ email: newsletterEmail }),
		})
			.then((res) => {
				console.log("Server response:", res); // debug log
				if (!res.ok) throw new Error("Newsletter signup failed");
				return res.json();
			})
			.then((data) => {
				console.log("Response data:", data); // debug log
				alert("Subscribed successfully");
				setNewsletterEmail("");
			})
			.catch((err) => {
				console.error("Error during newsletter signup:", err); // debug log
				alert(err.message);
			});
	};

	// Helper function to strip HTML tags and limit length for meta description
	const stripHtml = (html) => {
		return html.replace(/<[^>]+>/g, "").trim().substring(0, 160);
	};

	if (loading) return <div className="py-12 text-center">Loading...</div>;
	if (error)
		return <div className="py-12 text-center text-red-600">Error: {error}</div>;
	if (!blog) return <div className="py-12 text-center">Blog not found.</div>;

	return (
		<>
			<Helmet>
				<title>{blog.meta_title || blog.title}</title>
				<meta name="description" content={blog.meta_description || stripHtml(blog.description)} />
			</Helmet>
			<div className="container flex flex-col gap-8 px-4 py-8 mx-auto lg:flex-row">
				<div className="w-full p-6 bg-white rounded-lg shadow ">
					<img
						src={blog.image}
						alt={blog.title}
						className="w-full mb-4 rounded-lg"
					/>
					{blog.category && (
						<div className="mb-2 text-sm" style={{ color: "#A14D1F" }}>
							{blog.category}
						</div>
					)}
					<h1 className="mb-4 text-3xl font-bold" style={{ color: "#A14D1F" }}>
						{blog.title}
					</h1>
					<p className="mb-4" style={{ color: "#4A2F24" }}>
						{blog.date}
					</p>
					<p className="mb-4 font-bold" style={{ color: "#4A2F24" }}>
						{blog.headline}
					</p>
					<div
						className="mb-4 leading-relaxed"
						style={{ color: "#4A2F24" }}
						dangerouslySetInnerHTML={{
							__html: DOMPurify.sanitize(blog.description),
						}}
					/>
				</div>
				{/* Sidebar */}
				<div className="w-full space-y-6 lg:w-1/3">
					{/* Newsletter */}
					<div className="p-6 bg-white rounded-lg shadow">
						<h2 className="mb-4 text-lg font-bold" style={{ color: "#A14D1F" }}>
							Join Our Newsletter
						</h2>
						<p className="mb-4" style={{ color: "#4A2F24" }}>
							Sign up for our free newsletters to receive the latest news.
						</p>
						<form onSubmit={handleNewsletterSubmit}>
							<input
								type="email"
								placeholder="Enter Your Email Address"
								className="w-full p-2 mb-4 bg-white border border-gray-300 rounded"
								value={newsletterEmail}
								onChange={(e) => setNewsletterEmail(e.target.value)}
								required
							/>
							<button
								type="submit"
								className="w-full py-2 text-white rounded hover:bg-green-600"
								style={{ background: "#A14D1F" }}
							>
								Subscribe
							</button>
						</form>
					</div>
					{/* Social Share */}
					<div className="p-6 bg-white rounded-lg shadow">
						<h2 className="mb-4 text-lg font-bold" style={{ color: "#A14D1F" }}>
							Share
						</h2>
						<div className="flex justify-center space-x-8">
							<a
								href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`}
								target="_blank"
								rel="noopener noreferrer"
								className="text-blue-600 hover:text-blue-800"
							>
								<FaFacebook size={24} />
							</a>
							<a
								href={`https://twitter.com/intent/tweet?url=${window.location.href}&text=${blog.title}`}
								target="_blank"
								rel="noopener noreferrer"
								className="text-blue-400 hover:text-blue-600"
							>
								<FaTwitter size={24} />
							</a>
							<a
								href={`https://www.linkedin.com/shareArticle?mini=true&url=${window.location.href}&title=${blog.title}`}
								target="_blank"
								rel="noopener noreferrer"
								className="text-blue-700 hover:text-blue-900"
							>
								<FaLinkedin size={24} />
							</a>
							<a
								href={`https://pinterest.com/pin/create/button/?url=${window.location.href}&media=${blog.image}&description=${blog.title}`}
								target="_blank"
								rel="noopener noreferrer"
								className="text-red-600 hover:text-red-800"
							>
								<FaPinterest size={24} />
							</a>
							<a
								href={`https://api.whatsapp.com/send?text=${blog.title} ${window.location.href}`}
								target="_blank"
								rel="noopener noreferrer"
								className="text-green-600 hover:text-green-800"
							>
								<FaWhatsapp size={24} />
							</a>
							<a
								href={`https://www.instagram.com/?url=${window.location.href}`}
								target="_blank"
								rel="noopener noreferrer"
								className="text-pink-600 hover:text-pink-800"
							>
								<FaInstagram size={24} />
							</a>
						</div>
					</div>
					{/* Related Blogs */}
					<div className="p-6 bg-white rounded-lg shadow">
						<h2 className="mb-4 text-lg font-bold" style={{ color: "#A14D1F" }}>
							Related Blogs
						</h2>
						<ul className="space-y-4">
							{relatedBlogs.slice(0, 5).map(
								(
									relatedBlog // Only show the last 5 related blogs
								) => (
									<Link to={`/blog/${relatedBlog.slug}`} key={relatedBlog.id}>
										<li className="flex items-center gap-3 p-3 my-2 space-x-4 transition-shadow duration-300 rounded-lg shadow-lg hover:shadow-xl">
											<img
												src={relatedBlog.image}
												alt={relatedBlog.title}
												className="w-16 h-16 rounded-lg"
											/>
											<div>
												<h3
													className="text-sm font-semibold"
													style={{ color: "#A14D1F" }}
												>
													{relatedBlog.title}
												</h3>
												{/* <p className="text-sm" style={{ color: '#4A2F24' }}>{relatedBlog.date}</p> */}
											</div>
										</li>
									</Link>
								)
							)}
						</ul>
					</div>
					{/* Disqus Comment Section */}
					<div className="mt-6">
						<div id="disqus_thread"></div>
					</div>
				</div>
			</div>
		</>
	);
};

export default BlogDetail;
